// filepath: apps/web/src/admin/locations/edit.tsx
//
// ISS-515 — LocationEditScreen: PATCH /api/directory/locations/{id}.
//
// INV-7: the row_version from the latest GET response is carried
// in the PATCH body; the server rejects stale submissions with
// 409 row_version_mismatch -- the SPA surfaces a StaleVersionError
// with a Reload button.
//
// INV-8: a fresh X-Idempotency-Key per submit so a double-submit
// is replayed as 200 rather than producing a duplicate audit
// row.
//
// ISS-431-uatrzt2 React crash fix: the project opts out of
// react-router-dom (FR-UI-CABINET-1 AC-9); uses the project's
// own navigate() primitive.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  getLocation,
  listLocations,
  newIdempotencyKey,
  updateLocation,
  type Location,
} from "./api";
import { LocationForm, type LocationFormValues } from "./LocationForm";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "row_version_mismatch";
  }
  return false;
}

export interface LocationEditScreenProps {
  /** The route id from App.tsx's path-matching dispatch. */
  id?: string;
  /** Test override for the loader. */
  loader?: (id: string) => Promise<Location>;
  /** Test override for the parent-candidate fetcher. */
  parentsFetcher?: () => Promise<Location[]>;
  /** Test override for the submit pipeline. */
  submit?: (
    id: string,
    input: Parameters<typeof updateLocation>[1],
    key: string,
  ) => Promise<void>;
}

export function LocationEditScreen({
  id = "",
  loader,
  parentsFetcher,
  submit,
}: LocationEditScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [row, setRow] = useState<Location | null>(null);
  const [parents, setParents] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);
  const [parentError, setParentError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    setParentError(null);
    try {
      const l = loader ?? ((lid: string) => getLocation(lid));
      const r = await l(id);
      setRow(r);
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  const loadParents = useCallback(async () => {
    try {
      const f = parentsFetcher ?? (() => listLocations({ limit: 200 }));
      const rows = await f();
      setParents(rows);
    } catch {
      // Parent-list failure on the edit screen is non-fatal.
      // The form silently falls back to "Root only".
    }
  }, [parentsFetcher]);

  useEffect(() => {
    void load();
    void loadParents();
  }, [load, loadParents]);

  const onSubmit = useCallback(
    async (values: LocationFormValues) => {
      if (!row) return;
      setSubmitting(true);
      setError(null);
      setStale(false);
      setParentError(null);
      try {
        const key = newIdempotencyKey();
        const input = {
          parent_location_id: values.parent_location_id,
          name: values.name,
          code: values.code === "" ? null : values.code,
          row_version: row.row_version,
        };
        const submitFn = submit ?? updateLocation;
        await submitFn(row.id, input, key);
        navigate(`/admin/locations/${encodeURIComponent(row.id)}`);
      } catch (e) {
        // 409 row_version_mismatch -> StaleVersionError (OCC).
        // 422 with code='cycle_detected' (Go
        // internal/directory/location/service.go raises
        // ErrCycleDetected) -> parent field-level error.
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else if (isRowVersionConflict(e)) {
          setStale(true);
        } else if (
          e instanceof ApiError &&
          e.status === 422 &&
          e.code === "cycle_detected"
        ) {
          setParentError(
            t(
              "directory.locations.edit.parent_cycle",
              "Reparent would introduce a cycle.",
            ),
          );
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [row, submit, t],
  );

  if (loading) {
    return (
      <p data-testid="location-edit-loading">
        {t("directory.locations.edit.loading", "Loading location…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="location-edit-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="location-edit-error">
        {t("directory.locations.edit.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="location-edit-not-found">
        {t("directory.locations.edit.not_found", "Location not found")}
      </p>
    );
  }

  return (
    <>
      {stale ? (
        <StaleVersionError
          entityName={row.name}
          onReload={() => void load()}
          data-testid="location-edit-stale"
        />
      ) : null}
      <LocationForm
        heading={t("directory.locations.edit.title", "Edit location")}
        initial={{
          parent_location_id: row.parent_location_id,
          name: row.name,
          code: row.code ?? "",
        }}
        selfId={row.id}
        parentCandidates={parents}
        onSubmit={onSubmit}
        onCancel={() =>
          navigate(`/admin/locations/${encodeURIComponent(row.id)}`)
        }
        submitting={submitting}
        error={error}
        parentError={parentError}
        submitLabel={t("directory.locations.edit.submit", "Save")}
        loadingLabel={t("directory.locations.edit.submitting", "Saving…")}
      />
    </>
  );
}

export default LocationEditScreen;
